import { Link } from "react-router-dom";
import { Sprout, ShieldCheck, ScrollText, AlertTriangle } from "lucide-react";

const LegalSection = ({ n, title, children }) => (
    <section>
        <h3 className="font-display text-base font-bold text-[#2e3220]">
            {n}. {title}
        </h3>
        <div className="mt-1 space-y-2 text-[#5a6142]">{children}</div>
    </section>
);

const Terms = () => {
    return (
        <main
            data-testid="terms-page"
            className="relative z-10 mx-auto max-w-3xl px-6 py-16 md:px-10 md:py-20"
        >
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#2e3220]/10 bg-[#F4F1EA] px-3 py-1.5">
                <ShieldCheck
                    className="h-3.5 w-3.5 text-[#737a3d]"
                    strokeWidth={2.5}
                />
                <span className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                    Community Terms
                </span>
            </div>
            <h1 className="font-display text-4xl font-bold tracking-tight text-[#2e3220] sm:text-5xl">
                Lendly Community Terms &amp; Agreement
            </h1>
            <p className="mt-3 text-sm text-[#5a6142]">
                Last updated: February 2026
            </p>

            {/* --- Plain-English Community Terms (kept as-is) --- */}
            <div className="mt-10 space-y-8 text-sm leading-relaxed text-[#2e3220]">
                <section>
                    <h2 className="font-display text-xl font-bold">
                        1. Who Lendly is for
                    </h2>
                    <p className="mt-2 text-[#5a6142]">
                        Lendly is a peer-to-peer rental marketplace for verified
                        residents of the Easton Park neighborhood. By signing up, you
                        confirm that you live at the address you submitted and that
                        you&apos;re at least 18 years old.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl font-bold">
                        2. Renting an item
                    </h2>
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-[#5a6142]">
                        <li>Pay through Stripe at checkout — never in cash off-platform.</li>
                        <li>
                            Pick up and return items in the same condition you received
                            them. Normal wear and tear is fine; reckless damage isn&apos;t.
                        </li>
                        <li>
                            Refundable deposits (when set by the lender) are held by
                            Lendly and released after a successful return.
                        </li>
                    </ul>
                </section>

                <section>
                    <h2 className="font-display text-xl font-bold">
                        3. Listing an item
                    </h2>
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-[#5a6142]">
                        <li>List only items you own and can legally rent.</li>
                        <li>Photos and descriptions must be honest and current.</li>
                        <li>
                            Lendly takes a flat <strong>10%</strong> platform fee on
                            each completed rental. The remaining 90% is yours.
                        </li>
                    </ul>
                </section>

                <section>
                    <h2 className="font-display text-xl font-bold">
                        4. Trust &amp; safety
                    </h2>
                    <p className="mt-2 text-[#5a6142]">
                        We verify every member&apos;s neighborhood address. Disputes
                        can be reported in-app and we may suspend accounts that violate
                        community norms (no-shows, damage, harassment, off-platform
                        payment requests).
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl font-bold">5. Privacy</h2>
                    <p className="mt-2 text-[#5a6142]">
                        Your exact address is shared only after a rental is confirmed.
                        Profile pages show street name and neighborhood — never your
                        doorstep.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl font-bold">6. Liability</h2>
                    <p className="mt-2 text-[#5a6142]">
                        Lendly is the marketplace, not the insurer. Any rental is
                        between you and your neighbor. We strongly recommend lenders
                        confirm condition at pickup and renters report issues within 24
                        hours of return.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl font-bold">7. Changes</h2>
                    <p className="mt-2 text-[#5a6142]">
                        We may update these terms as Lendly grows. We&apos;ll notify
                        members in-app whenever something material changes.
                    </p>
                </section>

                <section className="rounded-2xl bg-[#737a3d]/10 p-5 text-[#2e3220]">
                    <p className="inline-flex items-center gap-2 font-display text-base font-bold">
                        <Sprout className="h-4 w-4 text-[#737a3d]" />
                        Be a Lendly Neighbor
                    </p>
                    <p className="mt-2 text-sm text-[#5a6142]">
                        Most of these terms boil down to one thing: treat neighbors and
                        their gear like you&apos;d want yours treated. Thanks for
                        keeping the community kind.
                    </p>
                </section>
            </div>

            {/* --- Full legal terms appendix --- */}
            <div className="mt-16">
                <div
                    role="note"
                    className="flex items-start gap-3 rounded-2xl border border-[#B56C5A]/40 bg-[#B56C5A]/10 p-4 text-[#7a3a30]"
                >
                    <AlertTriangle
                        className="mt-0.5 h-5 w-5 flex-shrink-0"
                        strokeWidth={2.25}
                    />
                    <p className="text-xs font-semibold leading-relaxed">
                        <span className="font-bold uppercase tracking-widest">
                            Draft — pending counsel review.
                        </span>{" "}
                        The legal terms below are a working draft modeled on standard
                        peer-to-peer marketplace TOS. They have not been reviewed by an
                        attorney. Lendly will publish a final version, reviewed by Texas
                        counsel, before any change in liability allocation.
                    </p>
                </div>

                <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#2e3220]/10 bg-[#F4F1EA] px-3 py-1.5">
                    <ScrollText
                        className="h-3.5 w-3.5 text-[#737a3d]"
                        strokeWidth={2.5}
                    />
                    <span className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                        Full Legal Terms
                    </span>
                </div>

                <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-[#2e3220]">
                    Lendly Terms of Service
                </h2>

                <div className="mt-8 space-y-7 text-sm leading-relaxed">
                    <LegalSection n="1" title="Agreement to Terms">
                        <p>
                            These Terms of Service (&quot;Terms&quot;) form a binding
                            agreement between you and Lendly, Inc., a Texas company
                            (&quot;Lendly,&quot; &quot;we,&quot; &quot;our,&quot; or
                            &quot;us&quot;). By creating an account, listing an item,
                            booking a rental, or otherwise accessing the Lendly website,
                            mobile site, or related services (collectively, the
                            &quot;Platform&quot;), you agree to be bound by these Terms.
                            If you do not agree, you may not use the Platform.
                        </p>
                    </LegalSection>

                    <LegalSection n="2" title="Lendly's Role; No Agency">
                        <p>
                            Lendly is a technology platform that allows members
                            (&quot;Lenders&quot;) to make personal items available for
                            short-term rental to other members (&quot;Renters&quot;).
                            <strong>
                                {" "}Lendly is not a party to any rental agreement between
                                Lenders and Renters and does not own, sell, resell,
                                furnish, deliver, manage, insure, or control any listed
                                item.
                            </strong>{" "}
                            Lendly is not an agent of any user. The rental contract is
                            formed solely between the Lender and the Renter. Any
                            disputes arising out of a rental are between those users,
                            though Lendly may, at its discretion and as a courtesy,
                            assist with dispute resolution.
                        </p>
                    </LegalSection>

                    <LegalSection n="3" title="Eligibility & Account">
                        <p>
                            To use the Platform you must (a) be at least 18 years old,
                            (b) have legal capacity to enter into binding contracts, (c)
                            reside at a verifiable address within the service area
                            (currently the Easton Park neighborhood of Austin, Texas,
                            ZIP 78744), and (d) not be barred from using the Platform
                            under applicable law. You agree to provide accurate, current
                            information and to keep it up to date. You are responsible
                            for all activity under your account and for safeguarding
                            your credentials.
                        </p>
                    </LegalSection>

                    <LegalSection n="4" title="Listings (Lenders)">
                        <p>You represent and warrant that you:</p>
                        <ul className="list-disc space-y-1 pl-5">
                            <li>
                                Own (or are legally authorized to rent out) every item
                                you list;
                            </li>
                            <li>
                                Will provide accurate descriptions, current photos, and a
                                fair condition rating;
                            </li>
                            <li>
                                Will maintain the item in safe working order and disclose
                                known defects;
                            </li>
                            <li>
                                Will not list items prohibited by Section 8
                                (Acceptable Use); and
                            </li>
                            <li>
                                Are solely responsible for any taxes, permits, or
                                licenses related to your listings.
                            </li>
                        </ul>
                    </LegalSection>

                    <LegalSection n="5" title="Bookings (Renters)">
                        <p>You represent and warrant that you:</p>
                        <ul className="list-disc space-y-1 pl-5">
                            <li>
                                Will use rented items only for their intended purpose and
                                in accordance with any safety instructions;
                            </li>
                            <li>
                                Will return items in the condition received, normal wear
                                excepted;
                            </li>
                            <li>
                                Will reimburse the Lender for any loss, theft, or damage
                                beyond normal wear (the &quot;Damage Obligation&quot;);
                                and
                            </li>
                            <li>
                                Will inspect items at pickup and report any pre-existing
                                damage to the Lender immediately.
                            </li>
                        </ul>
                    </LegalSection>

                    <LegalSection n="6" title="Fees, Payments & Refunds">
                        <p>
                            Lendly charges a platform service fee equal to{" "}
                            <strong>ten percent (10%)</strong> of each completed rental,
                            deducted from the Lender&apos;s payout. All payments are
                            processed by Stripe under the Stripe Connected Account
                            Agreement; Lendly does not store full card numbers. Refunds,
                            cancellations, and security deposits are governed by the
                            Lendly Refund Policy, incorporated by reference, and by
                            Stripe&apos;s payment processing terms. Lendly may revise
                            fees with thirty (30) days&apos; notice.
                        </p>
                    </LegalSection>

                    <LegalSection n="7" title="Damage, Loss & Disputes">
                        <p>
                            The Damage Obligation runs between Renter and Lender.
                            Lendly may, at its sole discretion, hold or release any
                            security deposit and may assist users in resolving disputes,
                            but Lendly does not guarantee any outcome and is not an
                            insurer, guarantor, or arbitrator of disputes. Users are
                            encouraged to maintain their own insurance.
                        </p>
                    </LegalSection>

                    <LegalSection n="8" title="Acceptable Use">
                        <p>You agree not to use the Platform to list, rent, or transmit:</p>
                        <ul className="list-disc space-y-1 pl-5">
                            <li>Firearms, ammunition, explosives, or other weapons;</li>
                            <li>
                                Illegal drugs, drug paraphernalia, or prescription
                                medication;
                            </li>
                            <li>
                                Alcohol, tobacco, or other age-restricted goods (except
                                where lawful and with proper licensing);
                            </li>
                            <li>
                                Hazardous, radioactive, biohazardous, or flammable
                                materials;
                            </li>
                            <li>
                                Items that infringe any patent, trademark, copyright,
                                trade secret, or other proprietary right;
                            </li>
                            <li>Stolen goods, or goods you do not have the right to rent;</li>
                            <li>
                                Counterfeit goods, replicas, or unauthorized reproductions;
                            </li>
                            <li>Live animals, plants, or perishable food;</li>
                            <li>
                                Any item the use of which would violate any local, state,
                                or federal law.
                            </li>
                        </ul>
                        <p>
                            You also agree not to (a) reverse-engineer or scrape the
                            Platform, (b) circumvent payment by transacting off-platform,
                            (c) harass, threaten, or defame other users, or (d) submit
                            false reviews or fraudulent listings.
                        </p>
                    </LegalSection>

                    <LegalSection n="9" title="Content License">
                        <p>
                            You retain ownership of the photos, descriptions, reviews,
                            and other content you submit (&quot;User Content&quot;). You
                            grant Lendly a worldwide, royalty-free, sublicensable, and
                            transferable license to host, store, reproduce, modify, and
                            display User Content solely for the purposes of operating,
                            developing, and promoting the Platform. You represent that
                            you own or have the right to grant this license.
                        </p>
                    </LegalSection>

                    <LegalSection n="10" title="Disclaimer of Warranties">
                        <p className="font-semibold uppercase tracking-wide text-[#2e3220]">
                            THE PLATFORM AND ALL LISTINGS, ITEMS, AND USER CONTENT ARE
                            PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE,&quot;
                            WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.
                        </p>
                        <p>
                            Lendly disclaims, to the fullest extent permitted by law,
                            all warranties, including implied warranties of
                            merchantability, fitness for a particular purpose,
                            non-infringement, accuracy, quiet enjoyment, and any
                            warranty arising from course of dealing or usage of trade.
                            Lendly does not warrant the quality, safety, condition,
                            legality, or accuracy of any listing, that any rental will
                            be uninterrupted, secure, or error-free, or that any user is
                            who they claim to be.
                        </p>
                    </LegalSection>

                    <LegalSection n="11" title="Limitation of Liability">
                        <p className="font-semibold uppercase tracking-wide text-[#2e3220]">
                            TO THE MAXIMUM EXTENT PERMITTED BY LAW, LENDLY AND ITS
                            OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE
                            LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
                            EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, LOST
                            REVENUE, LOST DATA, OR LOSS OF GOODWILL, ARISING OUT OF OR
                            RELATING TO THESE TERMS OR THE PLATFORM, EVEN IF ADVISED OF
                            THE POSSIBILITY OF SUCH DAMAGES.
                        </p>
                        <p className="font-semibold uppercase tracking-wide text-[#2e3220]">
                            LENDLY&apos;S TOTAL CUMULATIVE LIABILITY FOR ANY CLAIM
                            ARISING OUT OF OR RELATING TO THESE TERMS OR THE PLATFORM
                            SHALL NOT EXCEED THE GREATER OF (A) ONE HUNDRED U.S. DOLLARS
                            ($100) OR (B) THE TOTAL AMOUNT OF PLATFORM FEES YOU PAID TO
                            LENDLY IN THE TWELVE (12) MONTHS IMMEDIATELY PRECEDING THE
                            EVENT GIVING RISE TO THE CLAIM.
                        </p>
                        <p>
                            These limitations apply to all theories of liability,
                            whether based in contract, tort (including negligence),
                            statute, or otherwise. Some jurisdictions do not allow
                            certain limitations, in which case the limitations apply to
                            the fullest extent permitted by law.
                        </p>
                    </LegalSection>

                    <LegalSection n="12" title="Indemnification">
                        <p>
                            You agree to defend, indemnify, and hold harmless Lendly and
                            its affiliates, officers, directors, employees, and agents
                            from and against any claim, demand, loss, damage, cost, or
                            expense (including reasonable attorneys&apos; fees) arising
                            from or related to (a) your use of the Platform, (b) your
                            User Content, (c) your violation of these Terms or
                            applicable law, (d) your interactions with any other user,
                            including any rental, transaction, or dispute, or (e) any
                            item you listed or rented.
                        </p>
                    </LegalSection>

                    <LegalSection n="13" title="Suspension & Termination">
                        <p>
                            Lendly may suspend or terminate your account at any time,
                            with or without notice, for any reason, including suspected
                            fraud, off-platform transactions, repeated late returns,
                            damage complaints, or violations of these Terms. You may
                            close your account at any time. Sections that by their
                            nature should survive termination — including Sections
                            10–15 — will survive.
                        </p>
                    </LegalSection>

                    <LegalSection
                        n="14"
                        title="Mandatory Arbitration & Class Action Waiver"
                    >
                        <p className="font-semibold uppercase tracking-wide text-[#2e3220]">
                            PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL
                            RIGHTS.
                        </p>
                        <p>
                            You and Lendly agree that any dispute, claim, or controversy
                            arising out of or relating to these Terms or the Platform
                            (a &quot;Dispute&quot;) will be resolved by binding,
                            individual arbitration administered by the American
                            Arbitration Association (&quot;AAA&quot;) under its
                            Consumer Arbitration Rules. Arbitration will take place in
                            Travis County, Texas, before a single neutral arbitrator,
                            and judgment may be entered on the award in any court of
                            competent jurisdiction.
                        </p>
                        <p className="font-semibold uppercase tracking-wide text-[#2e3220]">
                            YOU AND LENDLY EACH WAIVE THE RIGHT TO A JURY TRIAL AND THE
                            RIGHT TO BRING OR PARTICIPATE IN ANY CLASS, COLLECTIVE, OR
                            REPRESENTATIVE ACTION. CLAIMS MAY BE BROUGHT ONLY ON AN
                            INDIVIDUAL BASIS.
                        </p>
                        <p>
                            You may opt out of arbitration within 30 days of first
                            accepting these Terms by sending written notice to
                            legal@lendly.com containing your name and a clear statement
                            that you wish to opt out. Notwithstanding the foregoing,
                            either party may bring claims of intellectual-property
                            infringement or seek injunctive relief in court.
                        </p>
                    </LegalSection>

                    <LegalSection n="15" title="Governing Law">
                        <p>
                            These Terms are governed by the laws of the State of Texas,
                            without regard to its conflict-of-laws principles. To the
                            extent any Dispute is not subject to arbitration, the
                            parties consent to the exclusive jurisdiction and venue of
                            the state and federal courts located in Travis County,
                            Texas.
                        </p>
                    </LegalSection>

                    <LegalSection n="16" title="Changes to These Terms">
                        <p>
                            Lendly may modify these Terms from time to time. Material
                            changes will be communicated by email or in-app notice at
                            least thirty (30) days before they take effect. Your
                            continued use of the Platform after the effective date
                            constitutes acceptance of the revised Terms.
                        </p>
                    </LegalSection>

                    <LegalSection n="17" title="Miscellaneous">
                        <p>
                            These Terms constitute the entire agreement between you and
                            Lendly regarding the Platform and supersede any prior
                            agreements. If any provision is held unenforceable, the
                            remaining provisions will continue in effect. Lendly&apos;s
                            failure to enforce a right is not a waiver. You may not
                            assign these Terms; Lendly may assign without restriction.
                            Notices to Lendly may be sent to legal@lendly.com.
                        </p>
                    </LegalSection>
                </div>
            </div>

            <div className="mt-12">
                <Link to="/" className="btn-secondary">
                    Back to Lendly
                </Link>
            </div>
        </main>
    );
};

export default Terms;
